import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Input, Row, Spin, Upload } from 'antd';
import { DeleteOutlined, PictureOutlined, UploadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { axiosErrorHandler, displayImageUploadErrorMessage, isConcurrentModificationError, showSuccessNotification } from '../../../../../swzutils';
import { userHasPermission } from "../../../../../swzutils/UserPermissionsUtil";
import API from "../../../../../API";
import ImageFileUpload from "../../../components/ImageFileUpload";
import { UploadFileTransformer } from "../../../api/Representation";
import { loggedInUserSelector } from "../../../../../store/slice/auth";
import { useAppSelector } from "../../../../../store";
const TabLogoDocumentHeader = ({ tenantId }) => {
    const [showSpinner, setShowSpinner] = useState(false);
    const [initComplete, setInitComplete] = useState(false); // See note below before <Upload /> component
    const [docHeader, setDocHeader] = useState(undefined);
    const [logoImageUploadFileId, setLogoImageUploadFileId] = useState(undefined);
    const [logoImageContentsUri, setLogoImageContentsUri] = useState(undefined);
    const [uploadedQuoteTermsPdfFile, setUploadedQuoteTermsPdfFile] = useState(undefined);
    const [existingQuoteTermsPdfFile, setExistingQuoteTermsPdfFile] = useState(undefined);
    const [removeQuoteTermsPdf, setRemoveQuoteTermsPdf] = useState(undefined);
    const [removeLogo, setRemoveLogo] = useState(undefined);
    const [form] = Form.useForm();
    const loggedInUser = useAppSelector(loggedInUserSelector);
    useEffect(() => {
        if (tenantId) {
            loadDocumentHeader();
        }
    }, [tenantId]);
    const loadDocumentHeader = () => {
        setShowSpinner(true);
        setInitComplete(false);
        axios.get(`/admin/v1/tenants/${tenantId}/document-header`).then(response => {
            const documentHeader = response.data;
            form.setFieldsValue({
                "headerText": documentHeader.headerText
            });
            if (documentHeader.logoImageId) {
                const cacheBust = new Date().getTime();
                setLogoImageContentsUri(`/admin/v1/tenants/${tenantId}/document-header/logo-image?${cacheBust}`);
            }
            if (documentHeader.quoteTermsPdfDocument) {
                applyExistingQuoteTermsPdf(documentHeader.quoteTermsPdfDocument);
            }
            setDocHeader(documentHeader);
        }).catch((error) => {
            if (error.response.status !== 404) {
                axiosErrorHandler(error);
            }
        }).finally(() => {
            setShowSpinner(false);
            setInitComplete(true);
        });
    };
    const applyExistingQuoteTermsPdf = (doc) => {
        const cacheBust = new Date().getTime();
        let contentsUri = `${API.origin()}/admin/v1/tenants/${tenantId}/document-header/quote-terms-pdf?${cacheBust}`;
        setExistingQuoteTermsPdfFile(UploadFileTransformer.fromDocumentResponse(doc, contentsUri));
    };
    const save = (formValues) => {
        setShowSpinner(true);
        const updateRequest = {
            "version": docHeader ? docHeader.version : undefined,
            "headerText": formValues.headerText,
            "logoUploadedFileId": logoImageUploadFileId,
            removeLogo,
            "quoteTermsPdfUploadFileId": uploadedQuoteTermsPdfFile?.response?.id,
            removeQuoteTermsPdf
        };
        const saveMethod = docHeader ? "put" : "post";
        axios(`/admin/v1/tenants/${tenantId}/document-header`, {
            method: saveMethod,
            data: updateRequest
        }).then((response) => {
            setDocHeader(response.data);
            const description = logoImageUploadFileId ? 'Logo and Document Header successfully updated' : 'Document Header successfully updated';
            showSuccessNotification(description);
        }).catch((error) => {
            if (isConcurrentModificationError(error)) {
                loadDocumentHeader();
            }
            axiosErrorHandler(error);
        }).finally(() => setShowSpinner(false));
    };
    const onLogoImageRemove = () => {
        setLogoImageUploadFileId(undefined);
        setRemoveLogo(true);
        setLogoImageContentsUri(undefined);
    };
    const onQuoteTermsPdfUploaded = (file) => {
        setRemoveQuoteTermsPdf(undefined);
        setUploadedQuoteTermsPdfFile(file);
    };
    const onQuoteTermsPdfRemoved = ( /*file: UploadFile*/) => {
        if (docHeader?.quoteTermsPdfDocument) {
            setRemoveQuoteTermsPdf(true);
            setExistingQuoteTermsPdfFile(undefined);
        }
        setUploadedQuoteTermsPdfFile(undefined);
    };
    return (_jsx(Spin, { spinning: showSpinner, children: _jsxs(Form, { layout: "vertical", form: form, onFinish: save, children: [_jsxs(Row, { gutter: 10, className: "no-margin", children: [" ", _jsx(Col, { xs: 24, md: 12, children: _jsx(Card, { title: "Logo", type: "inner", variant: "borderless", extra: _jsx(Button, { icon: _jsx(DeleteOutlined, {}), onClick: onLogoImageRemove, children: "Remove" }), children: _jsx(ImageFileUpload, { onUploadComplete: setLogoImageUploadFileId, contentsUri: logoImageContentsUri, icon: _jsx(PictureOutlined, {}) }) }) }), _jsx(Col, { xs: 24, md: 12, children: _jsx(Card, { title: "Document Header", type: "inner", variant: 'borderless', children: _jsx(Form.Item, { name: "headerText", label: "Name and Address", rules: [
                                        { required: true, message: 'Please input text to appear in the document header.' },
                                        { max: 1000, message: 'Please limit the name to 1000 characters.' }
                                    ], children: _jsx(Input.TextArea, { rows: 8, className: "text-right" }) }) }) })] }), _jsxs(Row, { gutter: 10, className: "no-margin", children: [" ", _jsx(Col, { xs: 24, children: _jsx(Card, { title: "Quote Terms and Conditions PDF", type: "inner", variant: 'borderless', children: initComplete &&
                                    _jsx(Upload, { name: "uploadFile", accept: ".pdf", listType: "picture", maxCount: 1, defaultFileList: existingQuoteTermsPdfFile ? [existingQuoteTermsPdfFile] : undefined, withCredentials: true, action: `${API.ORIGIN}/admin/v1/files`, onChange: (info) => {
                                            const status = info.file.status;
                                            if (status === 'done') {
                                                onQuoteTermsPdfUploaded(info.file);
                                            }
                                            else if (status === 'error') {
                                                displayImageUploadErrorMessage(info.file);
                                            }
                                        }, onRemove: onQuoteTermsPdfRemoved, children: _jsx(Button, { icon: _jsx(UploadOutlined, {}), children: "Upload Document" }) }) }) })] }), _jsx("p", { children: "\u00A0" }), userHasPermission(loggedInUser, ['admin:documentHeader:update']) &&
                    _jsxs(Row, { gutter: 10, className: "no-margin", children: [" ", _jsx(Col, { xs: 12, md: 6, children: _jsx(Button, { htmlType: "button", onClick: form.submit, block: true, type: "default", size: "large", children: "Update" }) })] })] }) }));
};
export default TabLogoDocumentHeader;
